/**
 * 极简展示模式
 *
 * 特点：
 * - 只显示标题和图标
 * - 信息密度最高
 * - 适合快速扫描
 * - 节省空间
 */

import { Check, Pencil, Trash2 } from "lucide-react";
import { memo } from "react";
import type { MouseEventHandler } from "react";

import { getFolderNameById, getSourceName, useFolders } from "@/modules/mine";
import type { Item } from "@/modules/mine/types";
import { formatFullDateTime, formatRelativeTime } from "@/shared/lib/date";

import { getItemTypeConfig } from "./display-helpers";

interface ItemHeadlineProps {
  item: Item;
  isActive?: boolean;
  selectionMode?: boolean;
  isSelected?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onStartSelection?: MouseEventHandler<HTMLButtonElement>;
  onEdit?: () => void;
  onDelete?: () => void;
}

function getWebsiteHostname(url?: string) {
  if (!url) {
    return "website";
  }

  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return getSourceName(url);
  }
}

export const ItemHeadline = memo(
  ({
    item,
    isActive,
    selectionMode,
    isSelected,
    onClick,
    onStartSelection,
    onEdit,
    onDelete,
  }: ItemHeadlineProps) => {
    // 获取文件夹列表
    const folders = useFolders();
    const itemFormat = item.format ?? "website";
    const typeConfig = getItemTypeConfig(itemFormat);
    const TypeIcon = typeConfig.icon;
    // Data processing
    const sourceName =
      itemFormat === "website"
        ? getWebsiteHostname(item.url)
        : getSourceName(item.url || "");
    const folderName = getFolderNameById(item.folder_id ?? "", folders);
    const createdAtLabel = formatRelativeTime(item.created_at) || "未知时间";
    const createdAtTitle = formatFullDateTime(item.created_at) || createdAtLabel;

    return (
      <div
        data-slot="item-headline"
        data-active={isActive}
        onClick={onClick}
        className={`
          group relative flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors duration-200
          ${
            isActive
              ? "bg-accent/50 text-foreground font-medium"
              : "text-foreground/80 hover:bg-sidebar-accent"
          }
        `}
      >
        {/* Icon */}
        <div className="relative flex size-6 shrink-0 items-center justify-center">
          {itemFormat === "image" && item.url ? (
            <>
              <TypeIcon size={14} className={typeConfig.color} />
              <img
                src={item.url}
                alt=""
                className="absolute inset-0 size-full rounded-sm object-cover"
                onError={(event) => {
                  event.currentTarget.hidden = true;
                }}
              />
            </>
          ) : item.website?.favicon_url ? (
            <img
              src={item.website?.favicon_url}
              alt="icon"
              className="h-4 w-4 object-contain opacity-80 group-hover:opacity-100"
            />
          ) : (
            <TypeIcon size={14} className={typeConfig.color} />
          )}
        </div>

        {/* Title */}
        <span className="min-w-0 truncate font-medium">
          {item?.title || "Untitled"}
        </span>

        {/* Meta Info (Source / Folder) */}
        <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground/60">
          <span>—</span>
          <span className="max-w-[150px] truncate">{sourceName}</span>
          <span className="opacity-50">·</span>
          <span className="max-w-[100px] truncate">{folderName}</span>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right Side: Meta swaps with Actions on Hover */}
        <div className="relative flex min-w-[80px] items-center justify-end">
          {/* Layer 1: Date (Visible by default, hidden on hover) */}
          <div
            className={`flex items-center text-xs text-muted-foreground/50 transition-opacity duration-200 ${
              selectionMode ? "opacity-0" : "group-hover:opacity-0"
            }`}
          >
            <span title={createdAtTitle}>{createdAtLabel}</span>
          </div>

          {/* Layer 2: Actions (Hidden by default, visible on hover) */}
          {(onEdit || onDelete || onStartSelection || selectionMode) && (
            <div
              className={`absolute right-0 flex items-center gap-1.5 transition-opacity duration-200 ${
                selectionMode
                  ? "pointer-events-none opacity-100"
                  : "opacity-0 group-hover:opacity-100"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {onEdit && (
                <button
                  type="button"
                  className="flex size-7 items-center justify-center rounded-md bg-muted/80 text-muted-foreground backdrop-blur-md transition-colors hover:bg-muted hover:text-foreground"
                  title="编辑"
                  onClick={onEdit}
                >
                  <Pencil size={13} strokeWidth={2.5} />
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  className="flex size-7 items-center justify-center rounded-md bg-muted/80 text-muted-foreground backdrop-blur-md transition-colors hover:bg-muted hover:text-red-600"
                  title="删除"
                  onClick={onDelete}
                >
                  <Trash2 size={13} strokeWidth={2.5} />
                </button>
              )}
              {onStartSelection && (
                <button
                  type="button"
                  aria-label="批量选择"
                  className="flex size-7 items-center justify-center"
                  title="批量选择"
                  onClick={onStartSelection}
                >
                  <span className="flex size-5 items-center justify-center rounded-md border-[1.5px] border-border/70 bg-background/90 text-transparent">
                    <Check className="size-3" strokeWidth={2.6} />
                  </span>
                </button>
              )}
              {selectionMode && (
                <div className="pointer-events-none flex size-7 items-center justify-center">
                  <span
                    className={`flex size-5 items-center justify-center rounded-md border-[1.5px] transition-colors duration-200 ${
                      isSelected
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border/70 bg-background/90 text-transparent"
                    }`}
                  >
                    <Check className="size-3" strokeWidth={2.6} />
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);
